import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { z } from 'zod';
import { eventsApi } from '@/features/events/api';
import { surveysApi } from '@/features/surveys/api';
import { queryKeys } from '@/lib/api/queryKeys';
import type { XkStation } from '@/lib/api/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { FormField } from '@/components/forms/FormField';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';

const editStationSchema = z.object({
  name: z.string().min(2, 'stations.editDialog.nameRequired'),
  surveysId: z.union([z.number().int().positive(), z.literal(0)]).optional(),
});
type EditStationForm = z.infer<typeof editStationSchema>;

interface EditStationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  station: XkStation;
  onDeleted?: () => void;
}

/**
 * Edit dialog for `xk_station`. Allows changing the display name and the
 * survey wired to the station. The backend auto-clones template surveys on
 * save (see xk-station.controller `POST/PUT` hook), so picking a template
 * just attaches a fresh copy scoped to the user's company.
 */
export function EditStationDialog({
  open,
  onOpenChange,
  station,
  onDeleted,
}: EditStationDialogProps) {
  const queryClient = useQueryClient();
  const t = useT();
  // Two-step delete confirm (see EditProjectDialog / EditEventDialog).
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Surveys eligible for this station: same brand (or templates).
  const surveysQ = useQuery({
    queryKey: queryKeys.surveys.all,
    queryFn: () => surveysApi.list({ productType: 'xotski' }),
  });
  const surveys = surveysQ.data?.items ?? [];

  const form = useForm<EditStationForm>({
    resolver: zodResolver(editStationSchema),
    defaultValues: {
      name: station.name,
      surveysId: station.surveysId ?? 0,
    },
  });

  useEffect(() => {
    form.reset({
      name: station.name,
      surveysId: station.surveysId ?? 0,
    });
  }, [station, form]);

  useEffect(() => {
    if (!open) setConfirmDelete(false);
  }, [open]);

  const update = useMutation({
    mutationFn: (v: EditStationForm) =>
      eventsApi.updateStation(station.id, {
        name: v.name,
        // 0 (the "no survey" option) → null so backend clears the link.
        surveysId: v.surveysId && v.surveysId > 0 ? v.surveysId : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.detail(station.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.byEvent(station.eventId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.all });
      toast({ tone: 'success', title: t('stations.editDialog.updatedToast') });
      onOpenChange(false);
    },
    onError: (e) =>
      toast({
        tone: 'error',
        title: t('stations.editDialog.updateFailed'),
        description: (e as Error).message,
      }),
  });

  const remove = useMutation({
    mutationFn: () => eventsApi.deleteStation(station.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.byEvent(station.eventId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.all });
      toast({ tone: 'success', title: t('stations.editDialog.deletedToast') });
      onOpenChange(false);
      onDeleted?.();
    },
    onError: (e) =>
      toast({
        tone: 'error',
        title: t('stations.editDialog.deleteFailed'),
        description: (e as Error).message,
      }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('stations.editDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('stations.editDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={form.handleSubmit((v) => update.mutate(v))}
        >
          <FormField
            label={t('stations.editDialog.stationName')}
            className="sm:col-span-2"
            required
            error={
              form.formState.errors.name?.message
                ? t(form.formState.errors.name.message)
                : undefined
            }
          >
            <Input {...form.register('name')} />
          </FormField>

          <FormField label={t('stations.editDialog.survey')} className="sm:col-span-2">
            <Select
              {...form.register('surveysId', { valueAsNumber: true })}
              disabled
              title={t('stations.editDialog.surveyDisabledTitle')}
            >
              <option value={0}>{t('stations.editDialog.noSurvey')}</option>
              {surveys.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.isTemplate ? ` (${t('stations.editDialog.template')})` : ''}
                  {s.companyId == null ? ` · ${t('stations.editDialog.global')}` : ''}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-[10px] text-[var(--color-fg-subtle)]">
              {t('stations.editDialog.surveyHintPre')}{' '}
              <strong>{t('stations.editDialog.surveyHintLink')}</strong>{' '}
              {t('stations.editDialog.surveyHintPost')}
            </p>
          </FormField>

          {confirmDelete && (
            <div className="sm:col-span-2 rounded-lg border border-[var(--color-error)] bg-[rgb(239_68_68/0.08)] p-3 text-xs text-[var(--color-fg-primary)]">
              <strong className="text-[var(--color-error)]">
                {t('stations.editDialog.confirmHeader')} "{station.name}"?
              </strong>{' '}
              {t('stations.editDialog.confirmBody')}{' '}
              {t('stations.editDialog.confirmInstruction')}{' '}
              <em>{t('stations.editDialog.confirmInstructionEmph')}</em>{' '}
              {t('stations.editDialog.confirmInstructionEnd')}
            </div>
          )}

          <div className="sm:col-span-2">
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  confirmDelete ? remove.mutate() : setConfirmDelete(true)
                }
                loading={remove.isPending}
                leftIcon={<Trash2 className="h-4 w-4" />}
              >
                {remove.isPending
                  ? t('stations.editDialog.deleting')
                  : confirmDelete
                    ? t('stations.editDialog.confirmDelete')
                    : t('stations.editDialog.delete')}
              </Button>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                {t('stations.editDialog.cancel')}
              </Button>
              <Button type="submit" loading={update.isPending}>
                {t('stations.editDialog.save')}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
